import React, { useCallback, useEffect, useState } from 'react';

import { useIsFocused } from '@react-navigation/native';
import { StyleSheet } from 'react-native';

import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { Typography } from 'components/ui';
import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { primitive } from 'styles/constants';
import { fontSizes } from 'styles/constants/fonts';

type Daypart = 'morning' | 'afternoon' | 'evening';

type Greeting = {
  defaultLabel: string;
  emoji: string;
  labelKey: string;
};

const greetingsByDaypart: Record<Daypart, Greeting> = {
  morning: {
    labelKey: 'homepageDaypartGreetingMorning',
    defaultLabel: 'Good Morning',
    emoji: '☕',
  },
  afternoon: {
    labelKey: 'homepageDaypartGreetingAfternoon',
    defaultLabel: 'Good Afternoon',
    emoji: '☀️',
  },
  evening: {
    labelKey: 'homepageDaypartGreetingEvening',
    defaultLabel: 'Good Evening',
    emoji: '🌙',
  },
};

const daypartBoundaryHours = [4, 12, 17];

const getMillisecondsUntilNextDaypart = (date: Date) => {
  const nextBoundaryHour = daypartBoundaryHours.find(hour => hour > date.getHours());
  const nextBoundary = new Date(date);

  if (nextBoundaryHour === undefined) {
    nextBoundary.setDate(nextBoundary.getDate() + 1);
    nextBoundary.setHours(daypartBoundaryHours[0], 0, 0, 0);
  } else {
    nextBoundary.setHours(nextBoundaryHour, 0, 0, 0);
  }

  return nextBoundary.getTime() - date.getTime();
};

export const getDaypart = (date: Date): Daypart => {
  const hour = date.getHours();

  if (hour >= 4 && hour < 12) {
    return 'morning';
  }

  if (hour >= 12 && hour < 17) {
    return 'afternoon';
  }

  return 'evening';
};

const buildGreeting = (daypart: Daypart, name?: string | null): string => {
  const { defaultLabel, labelKey } = greetingsByDaypart[daypart];
  const label = t(labelKey, defaultLabel);
  const firstName = name?.trim().split(' ')[0];

  if (!firstName) {
    return `${label}!`;
  }

  return t('homepageDaypartGreetingWithName', {
    defaultValue: '{greeting}, {name}!',
    greeting: label,
    name: firstName,
  });
};

export const getHomeDaypartGreeting = (date: Date, name?: string | null): string =>
  buildGreeting(getDaypart(date), name);

export const getHomeDaypartGreetingEmoji = (date: Date): string =>
  greetingsByDaypart[getDaypart(date)].emoji;

type HomeDaypartGreetingProps = {
  /** Allows deterministic rendering in component tests. Production callers omit this. */
  date?: Date;
};

export const HomeDaypartGreeting = ({ date }: HomeDaypartGreetingProps) => {
  const { loyaltyUser } = useLoyaltyUser();
  const isGreetingEnabled = useFlag(LaunchDarklyFlag.HOMEPAGE_DYNAMIC_DAYPART_GREETING);
  const isFocused = useIsFocused();
  const { addAppStateChangeListeners, removeAppStateChangeListeners } = useAppStateChangeListener();
  // Seeded from the device clock and re-read on focus, on foreground, and by the
  // scheduler below. Storing the daypart rather than a timestamp lets React drop
  // the update whenever the re-read lands on the same daypart.
  const [daypart, setDaypart] = useState<Daypart>(() => getDaypart(date ?? new Date()));

  const refreshDaypart = useCallback(() => setDaypart(getDaypart(new Date())), []);

  useEffect(() => {
    if (date || !isFocused || !isGreetingEnabled) {
      return;
    }

    refreshDaypart();

    let timeoutId: ReturnType<typeof setTimeout>;
    const scheduleNextDaypartCheck = () => {
      timeoutId = setTimeout(() => {
        refreshDaypart();
        scheduleNextDaypartCheck();
      }, getMillisecondsUntilNextDaypart(new Date()));
    };

    scheduleNextDaypartCheck();

    return () => clearTimeout(timeoutId);
  }, [date, isFocused, isGreetingEnabled, refreshDaypart]);

  useEffect(() => {
    if (date || !isFocused || !isGreetingEnabled) {
      return;
    }

    addAppStateChangeListeners(refreshDaypart);

    return () => removeAppStateChangeListeners();
  }, [
    addAppStateChangeListeners,
    date,
    isFocused,
    isGreetingEnabled,
    refreshDaypart,
    removeAppStateChangeListeners,
  ]);

  if (!isGreetingEnabled) {
    return null;
  }

  const activeDaypart = date ? getDaypart(date) : daypart;

  return (
    <CenteredRowContainer
      containerStyle={styles.container}
      standardPadding
      style={styles.content}
      testID="home-daypart-greeting"
    >
      <Typography fontFamily="heading" style={styles.greeting}>
        {buildGreeting(activeDaypart, loyaltyUser?.name)}
        <Typography style={styles.greeting} aria-hidden>
          {` ${greetingsByDaypart[activeDaypart].emoji}`}
        </Typography>
      </Typography>
    </CenteredRowContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: primitive.$mayoEggWhite,
  },
  content: {
    paddingVertical: 12,
  },
  greeting: {
    color: primitive.$bbqBrown,
    fontSize: fontSizes.xl,
    fontWeight: '400',
    lineHeight: fontSizes['2xl'],
  },
});
